import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ApiButton,
  Button,
  ContentTemplate,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Progress,
} from 'web-library';
import { useDispatch } from 'react-redux';
import { useResitNavigation } from '../../../../routes/ResitRoute';
import Instruction from './Instruction';
import { candidateService } from '../../../../api';
import WindowPeriodInformation from '../../../../components/cards/WindowPeriodInformation';
import { WindowPeriodByExamLevelResponse } from '../../../../api/candidate/resit/@coordinator/api-services/models';
import RouteKey from '../../../../routes/key';
import { UpdateApplicationUuid, UpdateBankTransferOption } from '../../../../store/resitSlice';
import { useTriggerMutate } from '../../../../hooks/query';
import { useStartCandidateResit } from '../../../../api-refactored/resit-registration/useStartCandidateResit';
import { usePostCheckEligibility } from '../../../../api-refactored/resit-registration/usePostCheckEligibility';
import { useDeleteExamRegistrationDraft } from '../../../../api/candidate/exam/@candidate/queries-services/queries';

const { error: errorRoute } = RouteKey.child;
const { generic, invalid: invalidRoute } = RouteKey.error.child;

const ResitInstruction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { accessToken, toResitFlow } = useResitNavigation();
  // time window state
  const [timeWindow, setTimeWindow] = useState<WindowPeriodByExamLevelResponse | undefined>();
  const [eligibleErrorCode, setEligibleErrorCode] = useState<string | undefined>();
  const [eligibleErrorData, setEligibleErrorData] = useState<string | undefined>();
  const [draftUuid, setDraftUuid] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  /**
   * check candidate eligibility + time window
   *  */
  const {
    mutate: postCheckEligibility,
    isSuccess: eligibleSuccess,
    isError: eligibleError,
  } = usePostCheckEligibility({
    onSuccess: (response) => {
      if (response.error) {
        if (response.error.code === '5004') {
          setTimeWindow({
            timeWindow: {
              openDateTime: (response.error.data?.openDateTime as unknown) as string,
              closeDateTime: (response.error.data?.closeDateTime as unknown) as string,
            },
            timeWindowOpen: true,
            code: response.error.code,
            message: response.error.message,
          });
        }
        setEligibleErrorCode(response.error.code);
        setEligibleErrorData(response.error.message);
      }

      if (response.draftExist) {
        setDraftUuid(response.uuid);
      }
    },
    onError: (err) => {
      setEligibleErrorCode(err.response?.data.code);
      setEligibleErrorData(err.response?.data.message);

      if (err.response?.data.code === '1007') {
        history.push(`/${errorRoute}/${generic}`);
      }
    },
  });

  /**
   * Check Access link for candidate only
   */
  const { mutate } = candidateService.resit.all.usePostValidateAccessLink(
    (res) => {
      // update redux
      dispatch(UpdateBankTransferOption(res.data.bankTransferEnabled));
      // by pass success
      // call eligiblity with time window by pass
      postCheckEligibility({ accessLink: !!accessToken });
    },
    () => {
      // fail mean invalid link, redirect
      history.push(`/${errorRoute}/${invalidRoute}`);
    }
  );

  const { mutate: startResitMutate, status: startResitStatus } = useStartCandidateResit({
    onSuccess: (data) => {
      dispatch(UpdateApplicationUuid(data.uuid));
      history.push(toResitFlow);
    },
  });

  const {
    mutate: deleteRegistrationDraft,
    status: deleteRegistrationDraftStatus,
  } = useDeleteExamRegistrationDraft(() => startResitMutate(accessToken));

  const callMutation = useCallback(() => {
    if (accessToken) {
      mutate({
        token: accessToken,
      });
    }
  }, [mutate, accessToken]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  const handleStart = () => {
    if (draftUuid) {
      setOpenDeleteDialog(true);
    } else {
      startResitMutate(accessToken);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      // normal
      postCheckEligibility({ accessLink: !!accessToken });
    } else {
      // try to by pass
      setTriggerMutate((prev) => prev + 1);
    }
  }, [accessToken, postCheckEligibility, setTriggerMutate]);

  if (eligibleError) {
    return (
      <ContentTemplate type="full" transparent={!!eligibleErrorData} continuous>
        <Grid xs={12}>
          <WindowPeriodInformation
            eligibleErrorCode={eligibleErrorCode}
            eligibleErrorData={eligibleErrorData}
            page="candidate"
            timeWindow={timeWindow?.timeWindow}
          />
        </Grid>
      </ContentTemplate>
    );
  }

  if (eligibleSuccess) {
    return (
      <ContentTemplate>
        <Instruction />

        <Grid xs={12} md={2}>
          <ApiButton
            fullWidth
            id="button-resitInstruction-start"
            status={startResitStatus}
            onClick={handleStart}
          >
            {t('resit->>ResitInstruction.Start')}
          </ApiButton>
        </Grid>

        {openDeleteDialog && (
          <Dialog open={openDeleteDialog} maxWidth="sm">
            <DialogContent>
              <DialogContentText>{t('resit->>ResitInstruction.DeleteMessage')}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined">
                {t('resit->>ResitInstruction.Cancel')}
              </Button>
              <ApiButton
                status={deleteRegistrationDraftStatus}
                type="button"
                onClick={() => deleteRegistrationDraft(draftUuid)}
              >
                {t('resit->>ResitInstruction.Proceed')}
              </ApiButton>
            </DialogActions>
          </Dialog>
        )}
      </ContentTemplate>
    );
  }

  // waiting api result
  return (
    <ContentTemplate transparent continuous>
      <Progress color="secondary" />
    </ContentTemplate>
  );
};

export default ResitInstruction;
